import { Link } from "react-router-dom";
import { fmtFull, roundDate } from "../hooks/useRounds";
import { enterPath } from "./RoundsGrid";

const fmtAmt = (n, dp = 0) => Number(n).toLocaleString("en-US", { minimumFractionDigits: dp, maximumFractionDigits: dp });

/*
  The wide "feature" round card (client, 2026-10-07: the Early Bird card's
  aesthetic, the offer itself is gone). Photo above the heading on the left,
  circuit, one line of copy, full-width CTA; hairline-divided right column with
  the date as the headline line, the fee as the big numeral, one k/v row.

  2026-10-08: shared between the landing (tone="light" -> black card on the
  light band) and the Ultimate Lap rounds section (tone="dark" -> white card on
  black), where it is the hero slot for the NEXT OPEN round above the timeline.

  2026-10-09 (client): everything on the card in CAPS, date written in full
  ("SATURDAY 24TH OCTOBER 2026"); the price stays numeric.

  The WHOLE card is the link when the round can be opened; the CTA is a span.

  Round lifecycle - four states, driven by the API's `entries` field, which
  is PER ROUND (client, 2026-10-20: several rounds may be open at once;
  `is_main` only decides which round sits in this slot):
    open    - entries open:   "Enter round N"  -> sign-up
    closed  - entries closed: "Entries closed, please contact management."
              + a full-size "Contact" button  -> /contact
    soon    - not opened yet: "Opening soon", not clickable
    done    - completed round: "See results"
*/
export function RoundFeatureCard({ round: r, tone = "light", testid = "rounds-card", extraClass = "", dataTestidCard }) {
  const done = !!r && r.status === "completed";
  const open = !!r && !done && r.entries === "open";
  const closed = !!r && !done && r.entries === "closed";
  const href = !r ? null : done ? "/events/ultimate-lap/results" : open ? enterPath(r.n) : closed ? "/contact" : null;
  const Card = href ? Link : "div";
  const cardProps = href ? { to: href, "aria-label": done ? "See results" : closed ? "Contact management" : `Enter round ${r.n}` } : {};
  const ctaLabel = !r ? "" : done ? "See results" : open ? `Enter round ${r.n}` : "Opening soon";
  const ctaDisabled = !!r && !href;
  const entriesK = !r ? "" : done ? "Round" : "Entries";
  const entriesV = !r ? "" : done ? "Completed" : open ? "Open now" : closed ? "Closed" : "Opening soon";
  return (
    <Card
      className={`eb-card eb-card--${tone} eb-card--compact eb-card--round eb-card--caps ${r && r.is_main ? "is-next" : "is-mono"} ${closed ? "is-closed" : ""} ${extraClass}`}
      data-reveal="fade"
      data-testid={dataTestidCard || testid}
      {...cardProps}
    >
      {r ? (
        <>
          <div className="eb-card__l">
            <div className="eb-card__photo" data-testid={`${testid}-photo`}>
              <img src={r.image} alt="" loading="eager" fetchPriority="low" decoding="async" />
            </div>
            <h3 className="eb-card__h" data-testid={`${testid}-round`}>Round {r.n}</h3>
            <p className="eb-card__circuit" data-testid={`${testid}-circuit`}>{r.circuit}</p>
            {/* Client, 2026-10-10: the "Time attack. Clear circuit..." tagline is gone. */}
            {closed && (
              <p className="eb-card__closed" data-testid={`${testid}-closed`}>Entries closed, please contact management.</p>
            )}
            <span className={`btn btn--fill eb-card__cta ${ctaDisabled ? "btn--disabled" : ""}`} aria-disabled={ctaDisabled ? "true" : undefined} data-testid={`${testid}-cta`}>
              {closed ? "Contact" : ctaLabel}
            </span>
          </div>
          <div className="eb-card__r">
            <div className="eb-card__row eb-card__row--line">
              <p className="tech-num eb-card__date" data-testid={`${testid}-date`}>
                <time dateTime={roundDate(r.date).toISOString()}>{fmtFull(r.date)}</time>
              </p>
            </div>
            {r.fee_aed > 0 && (
              <div className="eb-card__row">
                <div className="eb-card__price" data-testid={`${testid}-fee`}>
                  <span className="eb-card__cur">AED</span>
                  <span className="eb-card__num tech-num">{fmtAmt(r.fee_aed)}</span>
                  <span className="eb-card__vat">+{r.vat_pct ?? 5}% VAT</span>
                </div>
              </div>
            )}
            <div className="eb-card__row eb-card__row--diff">
              <span className="eb-card__k">{entriesK}</span>
              <span className="eb-card__diff tech-num" data-testid={`${testid}-status`}>{entriesV}</span>
            </div>
          </div>
        </>
      ) : (
        <div className="eb-card__l">
          <h3 className="eb-card__h">Rounds to be announced</h3>
        </div>
      )}
    </Card>
  );
}
